import React, { useCallback, useState } from "react";
import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Input,
    Textarea,
    useBoolean,
    FormControl,
    Select
} from '@chakra-ui/react';

import { MigrationScriptRequest } from "data/Migrate";
import { twMerge } from "tailwind-merge";
import { AiOutlineCloudUpload } from "react-icons/ai";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    company: { value: string; name: string }[];

    handleConfirmAddMigrationScript: (req: MigrationScriptRequest) => void;
};

const INITIAL_INPUT_MIGRATION_SCIRPT: MigrationScriptRequest = {
    tk: '',
    type: '',
    group: '',
    remark: '',
    detail: '',
    company: '',
    created_by: '',
    files: []
};

function ModalAddMigrationScript(props: Props) {
    const {
        isOpen,
        onClose,
        company,

        handleConfirmAddMigrationScript
    } = props;

    const handleClose = useCallback(() => {
        // Clean-up states
        setMigrationScriptRequest(INITIAL_INPUT_MIGRATION_SCIRPT);

        onClose();
    }, [onClose]);

    const [MigrationScriptRequest, setMigrationScriptRequest] = useState<MigrationScriptRequest>(INITIAL_INPUT_MIGRATION_SCIRPT);
    const [dragging, setDragging] = useState(false);

    const handleInputFieldsChange = <K extends keyof MigrationScriptRequest>(
        key: K,
        value: MigrationScriptRequest[K]
    ) => {
        setMigrationScriptRequest(prevState => ({
            ...prevState,
            [key]: value
        }));
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setDragging(true);
    };

    const handleDragLeave = () => {
        setDragging(false);
    };

    const handleDroppedFiles = useCallback((files: FileList | File[]) => {
        const newFiles = Array.from(files);

        setMigrationScriptRequest(prevState => {
            const existingNames = new Set(prevState.files.map(f => f.name));

            const filteredNewFiles = newFiles.filter(file => !existingNames.has(file.name));

            return {
                ...prevState,
                files: prevState.files.concat(filteredNewFiles)
            };
        });
    }, []);

    const handleFileInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (!event.target.files) return;

            handleDroppedFiles(event.target.files);
            event.target.value = '';
        },
        [handleDroppedFiles]

    );

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setDragging(false);

        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            handleDroppedFiles(event.dataTransfer.files);
            event.dataTransfer.clearData();
        }
    };

    const handleRemoveFile = (index: number) => {
        setMigrationScriptRequest(prevState => ({
            ...prevState,
            files: prevState.files.filter((_, i) => i !== index)
        }));
    };

    return (
        <Modal
            isCentered
            size="6xl"
            isOpen={isOpen}
            onClose={handleClose}
            closeOnOverlayClick={false}
            scrollBehavior="inside"
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader className="border-b-2">Add MigrationScript</ModalHeader>
                <ModalCloseButton className="border hover:bg-red-300" />
                <ModalBody>
                    <div className="flex flex-1 flex-row gap-5">
                        <div className="flex flex-1 flex-col gap-2 pl-4">
                            <div className="flex flex-row items-center">
                                <span className="w-1/4">
                                    Ticket <span className="text-red-500">*</span> :
                                </span>
                                <div className="flex-1">
                                    <Input
                                        placeholder="Ticket No. (กรุณาระบุ)"
                                        value={MigrationScriptRequest.tk}
                                        onChange={e => handleInputFieldsChange('tk', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-row items-center">
                                <span className="w-1/4">
                                    Company <span className="text-red-500">*</span> :
                                </span>
                                <FormControl className="flex-1">
                                    <Select
                                        size="sm"
                                        placeholder="Please choose one"
                                        value={MigrationScriptRequest.company}
                                        onChange={e => handleInputFieldsChange('company', e.target.value)}
                                    >
                                        {company.map((value, index) => (
                                            <option key={index} value={value.value}>
                                                {value.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <div className="flex flex-row items-center">
                                <span className="w-1/4">
                                    Type <span className="text-red-500">*</span> :
                                </span>
                                <FormControl className="flex-1">
                                    <Select
                                        size="sm"
                                        placeholder="Please choose one"
                                        value={MigrationScriptRequest.type}
                                        onChange={e => {
                                            handleInputFieldsChange('type', e.target.value);
                                            handleInputFieldsChange('group', e.target.value);
                                        }}
                                    >
                                        <option value="UatAndPrd">UAT&PRD </option>
                                        <option value="UatOnly">UAT ONLY</option>
                                        <option value="PrdOnly">PRD ONLY</option>
                                        <option value="PrdDataConfig">PRD (DataConfig)</option>
                                        <option value="PrdTableChange">PRD (TableChange)</option>
                                        <option value="Schemas">Schemas</option>
                                    </Select>
                                </FormControl>
                            </div>
                            <div className="flex flex-row items-center">
                                <span className="w-1/4">Detail : </span>
                                <Textarea
                                    rows={3}
                                    className="w-3/4 border"
                                    placeholder="กรุณากรอกรายละเอียดของ Detail"
                                    onChange={e => {
                                        e.persist();
                                        handleInputFieldsChange('detail', e.target.value)
                                    }}
                                />
                            </div>
                            <div className="flex flex-row items-center">
                                <span className="w-1/4">Remark : </span>
                                <Textarea
                                    rows={3}
                                    className="w-3/4 border"
                                    placeholder="กรุณากรอกรายละเอียดของ Remark (ถ้ามี)"
                                    onChange={e => {
                                        e.persist();
                                        handleInputFieldsChange('remark', e.target.value)
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex flex-1 flex-col gap-2">
                            <div className="flex flex-1 flex-row items-center">
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => document.getElementById("fileUpload")?.click()}
                                    className={twMerge(
                                        'cursor-pointer rounded-md border border-neutral-400 p-8 hover:shadow-md hover:shadow-neutral-300',
                                        dragging && 'bg-gray-100'
                                    )}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <AiOutlineCloudUpload className="text-sky-500" size={30} />
                                        <div className="flex flex-col">
                                            <span className="text-sm text-neutral-500">
                                                Drop or Browse
                                            </span>
                                            {MigrationScriptRequest.files.length > 0 && (
                                                <span className="text-xs text-orange-500">
                                                    Uploaded {MigrationScriptRequest.files.length} file(s)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>


                                <input
                                    type="file"
                                    multiple
                                    accept=".txt"
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                    id="fileUpload"
                                />
                            </div>
                            {MigrationScriptRequest.files.length > 0 && (
                                <div className="mt-2 w-full">
                                    <ul className="text-sm list-disc list-inside space-y-1">
                                        {MigrationScriptRequest.files.map((file, index) => (
                                            <li key={index} className="flex justify-between items-center gap-2">
                                                <span className="truncate">{file.name}</span>
                                                <Button
                                                    size="xs"
                                                    colorScheme="red"
                                                    variant="outline"
                                                    onClick={() => handleRemoveFile(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        colorScheme="green"
                        onClick={() => {
                            handleClose();
                            handleConfirmAddMigrationScript(MigrationScriptRequest);
                        }}
                    >
                        Confirm
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )

}

export default ModalAddMigrationScript